import { useCallback, useState, useEffect } from "react";
import { Auth } from "aws-amplify";
import {
  Link,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import { Helmet } from "react-helmet-async";
import { loginBg } from "../../assets/images";
import {
  githubIcon,
  infoIcon,
  logoIcon,
  showPasswordIcon,
  hidePasswordIcon,
} from "../../assets/icons";
import {
  API_RESPONSES,
  EMPTY_STRING,
  REGEX,
  ROUTES,
  USER_INFO,
} from "../../constants";
import { Alert } from "../../components/molecules";
import { selectAlert, setAlert } from "../../components/organisms/Alert";
import { fetchInvitedUsers } from "../UserProfile";
import classNames from "classnames";
import { get } from "lodash";
import { VALIDATION_MESSAGES, ALERT_MESSAGES } from "../../constants";
import { captureMessage } from "@sentry/react";

const { LOGIN, AUTH_CONFIRMATION } = ROUTES;
const { PASSWORD, WHITESPACES_AT_START, SPECIAL_CHARACTERS } = REGEX;
const { REACT_APP_GITHUB_CLIENT_ID } = process.env;
const USER_NAME_EXISTS_EXCEPTION = "UsernameExistsException";
const {
  VALIDATE_NAME,
  NAME_SHOULD_NOT_CONTAINS,
  NAME_REQUIRED,
  INVALID_EMAIL,
  EMAIL_REQUIRED,
  VALIDATE_ORGANIZATION_NAME,
  ORGANIZATION_NAME_SHOULD_NOT_CONTAINS,
  ORGANIZATION_REQUIRED,
  FIELD_EMPTY,
  MIN_PASSWORD_LENGTH,
  VALIDATE_PASSWORD,
  PASSWORD_SHOULD_CONTAIN,
  PASSWORD_NOT_MATCHED,
  MAX_CHAR_LENGTH,
  MIN_CHAR_LENGTH,
} = VALIDATION_MESSAGES;

const { USER_NOT_INVITED } = ALERT_MESSAGES;
const MAX_NAME_LENGTH = 25;
const MIN_NAME_LENGTH = 3;

const SignUp = ({ setIsSigningUp }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const selectedAlert = useSelector(selectAlert);
  const [alertData, setAlertData] = useState(selectedAlert);
  const [isUserConfirmed, setIsUserConfirmed] = useState(false);
  const [invitedUser, setInvitedUser] = useState();
  const [isValidInviteLink, setIsValidInviteLink] = useState(true);
  const [showPassword, setShouldShowPassword] = useState(false);
  const [showConfirmPassword, setShouldShowConfirmPassword] = useState(false);

  const [Params] = useSearchParams();
  const invitedUserId = Params.get("invitedUserId");

  useEffect(() => {
    if (invitedUserId) {
      dispatch(fetchInvitedUsers(invitedUserId))
        .then((res) => {
          if (res.payload) {
            setInvitedUser(res.payload);
            setIsValidInviteLink(
              res.payload.expiredAt > new Date().toISOString()
            );
          } else setIsValidInviteLink(false);
        })
        .catch((err) => {
          dispatch(
            setAlert({
              message: USER_NOT_INVITED,
              type: API_RESPONSES.ERROR,
            })
          );
        });
    }
  }, [invitedUserId, dispatch]);

  const user = {
    email: get(invitedUser, "email", EMPTY_STRING),
    organization: get(invitedUser, "organizationId", EMPTY_STRING),
    name: get(invitedUser, "name", EMPTY_STRING),
    password: EMPTY_STRING,
    confirmPassword: EMPTY_STRING,
  };

  useEffect(() => {
    setAlertData(selectedAlert);
  }, [selectedAlert]);

  const navigateTo = useCallback(
    (route) => navigate(route, { replace: true }),
    [navigate]
  );

  const validationSchema = Yup.object({
    name: Yup.string()
      .matches(WHITESPACES_AT_START, VALIDATE_NAME)
      .matches(SPECIAL_CHARACTERS, NAME_SHOULD_NOT_CONTAINS)
      .max(MAX_NAME_LENGTH, MAX_CHAR_LENGTH)
      .min(MIN_NAME_LENGTH, MIN_CHAR_LENGTH)
      .required(NAME_REQUIRED),
    email: Yup.string(INVALID_EMAIL)
      .email(INVALID_EMAIL)
      .required(EMAIL_REQUIRED),
    organization: Yup.string()
      .matches(WHITESPACES_AT_START, VALIDATE_ORGANIZATION_NAME)
      .matches(SPECIAL_CHARACTERS, ORGANIZATION_NAME_SHOULD_NOT_CONTAINS)
      .required(ORGANIZATION_REQUIRED),
    password: Yup.string()
      .required(FIELD_EMPTY)
      .min(8, MIN_PASSWORD_LENGTH)
      .test(
        VALIDATE_PASSWORD,
        PASSWORD_SHOULD_CONTAIN,
        (val) => val && PASSWORD.test(val)
      ),
    confirmPassword: Yup.string()
      .required(FIELD_EMPTY)
      .oneOf([Yup.ref("password"), null], PASSWORD_NOT_MATCHED),
  });

  const handleOnSubmit = useCallback(
    (values) => {
      Auth.signUp({
        username: values.email,
        password: values.password,
        attributes: { email: values.email },
        autoSignIn: { enabled: true },
      })
        .then((res) => {
          const { userSub } = res;
          const { name, email, organization } = values;
          let userInfo = {
            sub: userSub,
            name,
            email,
            organization,
            profilePicUrl: EMPTY_STRING,
            invitedBy: EMPTY_STRING,
          };
          if (invitedUserId) {
            delete userInfo.organization;
            userInfo = {
              ...userInfo,
              invitedUserId,
              organizationId: organization,
            };
          }
          sessionStorage.setItem(USER_INFO, JSON.stringify(userInfo));
          setIsSigningUp(true);
          navigateTo(AUTH_CONFIRMATION);
        })
        .catch((err) => {
          if (err.name === USER_NAME_EXISTS_EXCEPTION) setIsUserConfirmed(true);
          setAlertData({ message: err.message });
          const { pathname } = location;
          const detailedErrorInfo = {
            url: pathname,
            updatedBody: values,
          };
          captureMessage(
            `${err.message}\n${JSON.stringify(detailedErrorInfo)}`
          );
        });
    },
    [invitedUserId, setIsSigningUp, navigateTo, location]
  );

  return (
    <>
      <Helmet>
        <title>SignUp | TavaJS</title>
      </Helmet>
      <div className="auth-container min-h-screen relative flex items-center">
        <img
          src={loginBg}
          className="w-full h-full absolute top-0 left-0 object-cover pointer-events-none"
          alt="login-bg"
        />

        <div className="auth-box text-gray-50 relative w-full mx-auto py-12 px-6 sm:w-1/2 md:w-1/2 lg:w-1/3 2xl:w-1/4">
          {invitedUserId && !isValidInviteLink ? (
            <div className="auth-header">
              <div className="logo mb-6">
                <img src={logoIcon} width={52} alt="logo" />
              </div>
              <h1 className="text-3xl leading-9 font-extrabold mb-2">
                Incorrect or expired link.
              </h1>
              <span className="text-6 !font-normal">
                <strong>Pro Tip: </strong>Please ask admin to re-invite.
              </span>
            </div>
          ) : (
            <Formik
              initialValues={user}
              validationSchema={validationSchema}
              onSubmit={handleOnSubmit}
              enableReinitialize
            >
              {({
                values,
                handleSubmit,
                handleChange,
                handleBlur,
                touched,
                errors,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <div className="auth-header mb-8">
                    {alertData && (
                      <div className="my-2">
                        <Alert
                          type={alertData.type}
                          message={alertData.message}
                          handleOnClose={setAlertData}
                        />
                      </div>
                    )}
                    <div className="logo mb-6">
                      <img src={logoIcon} width={52} alt="logo-icon" />
                    </div>
                    <h1 className="text-3xl leading-9 font-extrabold mb-2">
                      {invitedUser
                        ? `Hi ${invitedUser.name}`
                        : `Let's Get Started`}
                    </h1>
                    <span
                      className={classNames("text-6", {
                        hidden: invitedUserId,
                      })}
                    >
                      Or&nbsp;
                      <Link
                        to={LOGIN}
                        className="text-indigo-200 hover:text-indigo-500"
                      >
                        login instead
                      </Link>
                    </span>
                  </div>
                  <div className="auth-body">
                    <div>
                      <div
                        className={classNames("form-group", {
                          hidden: invitedUserId,
                        })}
                      >
                        <label className="text-6 mb-1 block">
                          Continue with
                        </label>
                        <Link
                          to={`https://github.com/login/oauth/authorize?client_id=${REACT_APP_GITHUB_CLIENT_ID}&scope=user:email`}
                          className="btn btn-light block w-full text-center"
                        >
                          <img
                            src={githubIcon}
                            width={20}
                            className="mx-auto"
                            alt="github-icon"
                          />
                        </Link>
                      </div>
                      <div
                        className={classNames(
                          "separator flex items-center my-6",
                          { hidden: invitedUserId }
                        )}
                      >
                        <div className="flex-1 border-t border-white" />
                        <span className="text-sm leading-5 font-normal px-2">
                          Or continue with
                        </span>
                        <div className="flex-1 border-t border-white" />
                      </div>
                      <div
                        className={classNames("form-group mb-6", {
                          hidden: invitedUserId,
                        })}
                      >
                        <label className="text-sm font-medium mb-1 block">
                          Your Name
                        </label>
                        <Field
                          type="text"
                          className={classNames("form-control border-2", {
                            "border-red-600": touched.name && errors.name,
                          })}
                          placeholder="Your Name"
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {touched.name && errors.name && (
                          <div className="font-medium text-red-500 text-sm">
                            {errors.name}
                          </div>
                        )}
                      </div>
                      <div
                        className={classNames("form-group mb-6", {
                          hidden: invitedUserId,
                        })}
                      >
                        <label className="text-sm font-medium mb-1 block">
                          Email Id
                        </label>
                        <Field
                          type="text"
                          className={classNames("form-control border-2", {
                            "border-red-600": touched.email && errors.email,
                          })}
                          placeholder="Your email id"
                          name="email"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={(e) => {
                            handleBlur(e);
                            setIsUserConfirmed(false);
                          }}
                        />
                        {touched.email && errors.email && (
                          <div className="font-medium text-red-500 text-sm absolute">
                            {errors.email}
                          </div>
                        )}
                        {isUserConfirmed && (
                          <Link
                            to={`${AUTH_CONFIRMATION}?email=${encodeURIComponent(
                              values.email
                            )}`}
                            className="text-6 ml-auto text-indigo-200 flex items-center hover:text-indigo-500 mt-3"
                          >
                            <img
                              src={infoIcon}
                              alt="info-icon"
                              className="w-5 h-5 mr-1"
                            />
                            <span>Click here to confirm your Email</span>
                          </Link>
                        )}
                      </div>
                      <div
                        className={classNames("form-group mb-6", {
                          hidden: invitedUserId,
                        })}
                      >
                        <label className="text-sm font-medium mb-1 block">
                          Organization
                        </label>
                        <Field
                          type="text"
                          className={classNames("form-control border-2", {
                            "border-red-600":
                              touched.organization && errors.organization,
                          })}
                          placeholder="Your Organization"
                          name="organization"
                          value={values.organization}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {touched.organization && errors.organization && (
                          <div className="font-medium text-red-500 text-sm absolute">
                            {errors.organization}
                          </div>
                        )}
                      </div>
                      <div className="form-group mb-6">
                        <label className="text-sm font-medium mb-1 block">
                          Password
                        </label>
                        <div
                          className={classNames(
                            "flex  items-center bg-[#E8F0FE]  focus-within:border-indigo-500 border-2 rounded-md",
                            {
                              "border-red-600":
                                touched.password && errors.password,
                            }
                          )}
                        >
                          <Field
                            type={showPassword ? "text" : "password"}
                            className="form-control border-0 rounded-none !ring-0"
                            placeholder="Your Password"
                            name="password"
                            value={values.password}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <img
                            className="w-8 h-4 cursor-pointer"
                            src={
                              showPassword ? showPasswordIcon : hidePasswordIcon
                            }
                            alt="Password"
                            onClick={() => setShouldShowPassword(!showPassword)}
                          />
                        </div>
                        {touched.password && errors.password && (
                          <div className={"mb-9"}>
                            <span className="font-medium text-red-500 text-sm absolute">
                              {errors.password}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="form-group mb-6">
                        <label className="text-sm font-medium mb-1 block">
                          Confirm Password
                        </label>
                        <div
                          className={classNames(
                            "flex flex-between items-center bg-[#E8F0FE]  focus-within:border-indigo-500 border-2 rounded-md",
                            {
                              "border-red-600":
                                touched.confirmPassword &&
                                errors.confirmPassword,
                            }
                          )}
                        >
                          <Field
                            type={showConfirmPassword ? "text" : "password"}
                            className="form-control border-0 rounded-none !ring-0"
                            placeholder="Confirm Password"
                            name="confirmPassword"
                            value={values.confirmPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <img
                            className="w-8 h-4 cursor-pointer"
                            src={
                              showConfirmPassword
                                ? showPasswordIcon
                                : hidePasswordIcon
                            }
                            alt="Password"
                            onClick={() =>
                              setShouldShowConfirmPassword(!showConfirmPassword)
                            }
                          />
                        </div>
                        {touched.confirmPassword && errors.confirmPassword && (
                          <div className="font-medium text-red-500 text-sm absolute">
                            {errors.confirmPassword}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      className={
                        "btn btn-primary block w-full text-center mt-10"
                      }
                      type="submit"
                    >
                      {invitedUserId ? "Set Password" : "Create Account"}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </div>
      </div>
    </>
  );
};

export default SignUp;
